/**
 * The dashboard's figures, from `GET /api/v1/dashboard/`.
 *
 * One call for the whole screen rather than one per card: the counts, both
 * monthly series and the newest clients all arrive together, so the page has a
 * single load to wait on and a single retry to offer. Nothing on the screen is
 * a stand-in any more - the earnings, the conversion split and all three
 * welcome-card highlights are this payload too.
 *
 * What this deliberately does not do is turn a `null` count into a zero. The
 * backend sends null for a figure it has nothing to count, and a "0" on a tile
 * would read as a measurement rather than as a figure nobody has yet.
 *
 * On top of the bearer token this needs a completed workspace onboarding, the
 * same as the clients endpoints; the backend answers 403 without one, which
 * arrives as an `ApiError` carrying its own message.
 */

import { api, API_ENDPOINTS, toLookupReferences } from "../../../lib/api";
import type { GetToken } from "../../auth/types/auth.types";
import {
  engagementLength,
  formatShortDate,
  fullNameOf,
  statusOf,
} from "../../clients/clientMapping";
import type { Client } from "../../clients/types/clients.types";
import { MONTHS } from "../types/dashboard.types";
import type {
  DashboardResponse,
  DashboardSummary,
  MonthlyClients,
  MonthlyClientsResponse,
  MonthlyIncome,
  MonthlyIncomeResponse,
  RecentClientResponse,
} from "../types/dashboard.types";

/** A count the backend actually measured, or `null` for one it did not. */
const toCount = (value: number | null | undefined): number | null =>
  typeof value === "number" && Number.isFinite(value) ? value : null;

/**
 * Rupees out of the decimal string the backend keeps money in. `null` for an
 * amount it did not send, which is the same "no figure" the counts have - an
 * unparseable one lands there too rather than painting "₹ NaN" onto a card.
 */
const toMoney = (value: string | null | undefined): number | null => {
  if (typeof value !== "string" || !value.trim()) return null;
  const amount = Number(value);
  return Number.isFinite(amount) ? amount : null;
};

/**
 * One of the newest clients, as a row of the recent clients table.
 *
 * The same reading the My clients list gives the same fields, through the same
 * two helpers - the two tables show one record and must not disagree about what
 * it says.
 */
const toRecentClient = (response: RecentClientResponse): Client => ({
  id: response.public_id,
  name: fullNameOf({
    firstName: response.first_name ?? "",
    lastName: response.last_name ?? "",
  }),
  // `goals` now, `goal` on a payload the backend has not migrated - and either
  // way as a list, since a client may be working towards more than one. See
  // `toLookupReferences`.
  goals: toLookupReferences(response.goals ?? response.goal)
    .map((goal) => goal.name.trim())
    .filter(Boolean),
  activity: engagementLength(response.current_engagement_duration),
  joinedOn: formatShortDate(response.created_at),
  // No `archived_at` in this payload, so an unrecognised status falls back to
  // "Active" - these are the newest clients, not the archived ones.
  status: statusOf(response.status),
  // The two fields the My clients rows wear their flags from. This payload
  // carries the moment but no pre-consultation forms at all, so the form is
  // honestly `null` rather than guessed at - and the dashboard's own table
  // draws neither flag anyway. See `clientNews`.
  createdAt: response.created_at ?? "",
  submissionKey: null,
  submittedAt: null,
});

/** A row with a month named on it; anything else names no point on an axis. */
const named = <T extends { month?: unknown }>(row: T | null | undefined) =>
  !!row && typeof row.month === "string" && !!row.month.trim();

/**
 * One month of the New clients chart. A row with no month names no point on
 * the axis, so it is dropped rather than plotted under a blank label.
 */
const toMonthlyClients = (
  rows: MonthlyClientsResponse[] | null | undefined
): MonthlyClients[] =>
  (rows ?? [])
    .filter(named)
    .map((row) => ({ month: row.month, clients: toCount(row.count) ?? 0 }));

/**
 * One month of the earnings chart, with the past told from what is still to
 * come.
 *
 * Which month is which is decided by the name rather than by the row's place in
 * the list, so a payload that arrives short or out of order still colours
 * correctly. A month the app cannot place at all is treated as past: it has an
 * amount against it, and drawing that as the grey "no data" stub would deny a
 * figure the backend did send.
 */
const monthStatus = (month: string, today: Date): MonthlyIncome["status"] => {
  const index = MONTHS.indexOf(month.trim().slice(0, 3));
  if (index < 0 || index < today.getMonth()) return "past";
  return index === today.getMonth() ? "current" : "future";
};

const toMonthlyIncome = (
  rows: MonthlyIncomeResponse[] | null | undefined,
  today: Date = new Date()
): MonthlyIncome[] =>
  (rows ?? []).filter(named).map((row) => ({
    month: row.month,
    amount: toMoney(row.amount) ?? 0,
    status: monthStatus(row.month, today),
  }));

/** The dashboard, in the app's own casing. */
export async function fetchDashboard(
  getToken: GetToken,
  signal?: AbortSignal
): Promise<DashboardSummary> {
  const token = await getToken();
  if (!token) {
    // Not an ApiError: the server was never asked. Calling this without an
    // active session is a bug at the call site, not something to show a user.
    throw new Error("No active Clerk session - cannot load the dashboard.");
  }

  const response = await api.get<DashboardResponse>(
    API_ENDPOINTS.dashboard.summary,
    { token, signal }
  );

  return {
    totalClients: toCount(response?.total_clients),
    activeClients: toCount(response?.active_clients),
    conversionRate: toCount(response?.client_conversion_rate),
    plansCreated: toCount(response?.plans_created),
    plansCreatedInLast7Days: toCount(response?.plans_created_in_7_days),
    totalRequests: toCount(response?.total_requests),
    newRequestsInLast7Days: toCount(response?.new_requests_in_7_days),
    renewalsInNext7Days: toCount(response?.renewals_in_7_days),
    totalIncome: toMoney(response?.total_income),
    incomeByMonth: toMonthlyIncome(response?.income_by_month),
    newClientsByMonth: toMonthlyClients(response?.new_clients_by_month),
    recentClients: (response?.recent_clients ?? []).map(toRecentClient),
  };
}
